import { createFileRoute } from '@tanstack/react-router';
import { Download } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { DateInput } from '@/components/date-input';
import { Checkbox, Field, NativeSelect } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import { apiDownload } from '@/lib/api';
import { todayIso } from '@/lib/dates';
import {
  EXPORT_LISTS,
  EXPORT_PERIODS,
  EXPORT_REPORTS,
  exportListName,
  exportReportName,
  exportSummary,
  journalExportPath,
  listExportPath,
  reportDates,
  reportExportPath,
  type ExportFormat,
  type ExportListSpec,
} from '@/lib/export';
import { periodLabel } from '@/features/reports/settings';
import { useT } from '@/lib/i18n';
import { useOrgId } from '@/lib/org';
import { useCompanySettings } from '@/lib/settings';

export const Route = createFileRoute('/settings/export')({ component: ExportData });

const FORMATS: ExportFormat[] = ['xlsx', 'csv'];

/** One download after another; the browser would otherwise drop all but the first. */
async function downloadAll(
  jobs: Array<{ name: string; path: string; fallback: string }>,
): Promise<void> {
  let done = 0;
  const failed: string[] = [];
  for (const job of jobs) {
    try {
      await apiDownload(job.path, job.fallback);
      done += 1;
    } catch {
      failed.push(job.name);
    }
  }
  toast(exportSummary(done, failed), { tone: failed.length ? 'error' : 'success' });
}

/** Settings › Export data (document 06 G5): reports and lists to Excel or CSV, the full journal. */
function ExportData() {
  const t = useT();
  const orgId = useOrgId();
  return (
    <>
      <PageHeader
        title={t('settingsPages.data.export.title')}
        description={t('settingsPages.data.export.description')}
      />
      {!orgId ? (
        <EmptyState title={t('shell.selectClient')} hint={t('home.selectClientHint')} />
      ) : (
        <div className="grid gap-4 xl:grid-cols-2">
          <ReportsSection orgId={orgId} />
          <ListsSection orgId={orgId} />
          <JournalSection orgId={orgId} />
        </div>
      )}
    </>
  );
}

function Section({
  title,
  description,
  children,
  testId,
}: {
  title: string;
  description: string;
  children: ReactNode;
  testId: string;
}) {
  return (
    <section className="flex flex-col gap-4 rounded-lg border bg-card p-5" data-testid={testId}>
      <div>
        <h2 className="text-base font-semibold">{title}</h2>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      {children}
    </section>
  );
}

function FormatSelect({
  value,
  onChange,
  label,
}: {
  value: ExportFormat;
  onChange: (f: ExportFormat) => void;
  label: string;
}) {
  const t = useT();
  return (
    <Field label={label} className="w-[180px]">
      {(id) => (
        <NativeSelect
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value as ExportFormat)}
        >
          {FORMATS.map((v) => (
            <option key={v} value={v}>
              {t(`settingsPages.data.export.format.${v}`)}
            </option>
          ))}
        </NativeSelect>
      )}
    </Field>
  );
}

function ReportsSection({ orgId }: { orgId: string }) {
  const t = useT();
  const company = useCompanySettings();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [period, setPeriod] = useState('this_year_to_date');
  const [format, setFormat] = useState<ExportFormat>('xlsx');
  const [busy, setBusy] = useState(false);
  const all = picked.size === EXPORT_REPORTS.length;
  const toggle = (key: string) =>
    setPicked((p) => {
      const n = new Set(p);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });

  async function run() {
    const fy = company.data?.advanced.fiscalYearStart ?? 1;
    const today = todayIso();
    setBusy(true);
    try {
      await downloadAll(
        EXPORT_REPORTS.filter((r) => picked.has(r.key)).map((r) => ({
          name: exportReportName(r),
          path: reportExportPath(orgId, r, reportDates(r, period, today, fy), format),
          fallback: `${r.key}.${format}`,
        })),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section
      title={t('settingsPages.data.export.reportsTitle')}
      description={t('settingsPages.data.export.reportsDescription')}
      testId="export-reports"
    >
      <Checkbox
        label={
          all ? t('settingsPages.data.export.clearAll') : t('settingsPages.data.export.selectAll')
        }
        checked={all}
        onChange={() => setPicked(all ? new Set() : new Set(EXPORT_REPORTS.map((r) => r.key)))}
      />
      <div className="grid gap-2 sm:grid-cols-2">
        {EXPORT_REPORTS.map((r) => (
          <Checkbox
            key={r.key}
            label={exportReportName(r)}
            checked={picked.has(r.key)}
            onChange={() => toggle(r.key)}
          />
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <Field label={t('settingsPages.data.export.reportPeriod')} className="w-[220px]">
          {(id) => (
            <NativeSelect id={id} value={period} onChange={(e) => setPeriod(e.target.value)}>
              {EXPORT_PERIODS.map((p) => (
                <option key={p.key} value={p.key}>
                  {periodLabel(p)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <FormatSelect
          label={t('settingsPages.data.export.formatLabel')}
          value={format}
          onChange={setFormat}
        />
        <Button
          type="button"
          onClick={() => void run()}
          disabled={busy || picked.size === 0}
          className="gap-2"
          data-testid="export-reports-button"
        >
          <Download className="size-4" />
          {busy
            ? t('settingsPages.data.export.exporting')
            : picked.size
              ? t('settingsPages.data.export.exportReportsCount', { count: picked.size })
              : t('settingsPages.data.export.exportReports')}
        </Button>
      </div>
    </Section>
  );
}

function ListsSection({ orgId }: { orgId: string }) {
  const t = useT();
  const [picked, setPicked] = useState<Set<ExportListSpec['key']>>(new Set());
  const [format, setFormat] = useState<ExportFormat>('xlsx');
  const [busy, setBusy] = useState(false);
  const toggle = (key: ExportListSpec['key']) =>
    setPicked((p) => {
      const n = new Set(p);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });

  async function run() {
    setBusy(true);
    try {
      await downloadAll(
        EXPORT_LISTS.filter((l) => picked.has(l.key)).map((l) => ({
          name: exportListName(l),
          path: listExportPath(orgId, l.key, format),
          fallback: `${l.key}.${format}`,
        })),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section
      title={t('settingsPages.data.export.listsTitle')}
      description={t('settingsPages.data.export.listsDescription')}
      testId="export-lists"
    >
      <div className="grid gap-2 sm:grid-cols-2">
        {EXPORT_LISTS.map((l) => (
          <Checkbox
            key={l.key}
            label={exportListName(l)}
            checked={picked.has(l.key)}
            onChange={() => toggle(l.key)}
          />
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <FormatSelect
          label={t('settingsPages.data.export.formatLabel')}
          value={format}
          onChange={setFormat}
        />
        <Button
          type="button"
          onClick={() => void run()}
          disabled={busy || picked.size === 0}
          className="gap-2"
          data-testid="export-lists-button"
        >
          <Download className="size-4" />
          {busy
            ? t('settingsPages.data.export.exporting')
            : picked.size
              ? t('settingsPages.data.export.exportListsCount', { count: picked.size })
              : t('settingsPages.data.export.exportLists')}
        </Button>
      </div>
    </Section>
  );
}

function JournalSection({ orgId }: { orgId: string }) {
  const t = useT();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [format, setFormat] = useState<ExportFormat>('csv');
  const [busy, setBusy] = useState(false);
  const bad = Boolean(from && to && from > to);

  async function run() {
    setBusy(true);
    try {
      await downloadAll([
        {
          name: t('settingsPages.data.export.journalTitle'),
          path: journalExportPath(orgId, { from, to }, format),
          fallback: `journal.${format}`,
        },
      ]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section
      title={t('settingsPages.data.export.journalTitle')}
      description={t('settingsPages.data.export.journalDescription')}
      testId="export-journal"
    >
      <div className="flex flex-wrap items-end gap-3">
        <Field label={t('settingsPages.data.export.from')} className="w-[160px]">
          {(id) => <DateInput id={id} value={from} onChange={setFrom} />}
        </Field>
        <Field
          label={t('settingsPages.data.export.to')}
          className="w-[160px]"
          error={bad ? t('settingsPages.data.export.endBeforeStart') : undefined}
        >
          {(id) => <DateInput id={id} value={to} onChange={setTo} invalid={bad} />}
        </Field>
        <FormatSelect
          label={t('settingsPages.data.export.formatLabel')}
          value={format}
          onChange={setFormat}
        />
        <Button
          type="button"
          onClick={() => void run()}
          disabled={busy || bad}
          className="gap-2"
          data-testid="export-journal-button"
        >
          <Download className="size-4" />
          {busy
            ? t('settingsPages.data.export.exporting')
            : t('settingsPages.data.export.exportJournal')}
        </Button>
      </div>
    </Section>
  );
}
